import { Autocomplete, TextField } from '@mui/material'
import { useEffect, useState } from 'react'

interface Props {
  label?: string
  size?: 'small' | 'medium'
  value?: string[]
  options: string[]
  handleChange: (value?: string) => void
  dataCy?: string
}

export const PositionRangeAutocomplete = ({
  label,
  size,
  value,
  options,
  handleChange,
  dataCy,
}: Props) => {
  const [fieldValues, setFieldValues] = useState<string[]>([])

  useEffect(() => {
    if (value) setFieldValues(value)
  }, [value])

  const handleValueChange = (value?: string[] | null) => {
    if (!value || value.length === 0) {
      setFieldValues([])
      handleChange()
      return
    }

    let newValue

    if (value && value.length > fieldValues.length) {
      const indexes = value.map(v => options.indexOf(v)).sort((a, b) => a - b)
      const startIndex = indexes[0]
      const endIndex = indexes[indexes.length - 1] + 1

      newValue = options.slice(startIndex, endIndex)
    } else {
      const indexes = fieldValues.map(v => options.indexOf(v)).sort((a, b) => a - b)
      const minIndex = indexes[0]
      const maxIndex = indexes[indexes.length - 1]

      if (value && value.includes(options[minIndex])) {
        newValue = [options[minIndex]]
      } else {
        newValue = [options[maxIndex]]
      }
    }
    setFieldValues(newValue)
    handleChange(getRangeDisplayValue(newValue))
  }

  const getRangeDisplayValue = (values: string[]) => {
    if (values.length === 1) return values[0]

    const minValue = values[0]
    const maxValue = values[values.length - 1]

    return `${minValue}-${maxValue}`
  }

  const getRangeDisabledOptions = (option: string) => {
    if (fieldValues.length <= 1) return false

    const minValue = fieldValues[0]
    const maxValue = fieldValues[fieldValues.length - 1]

    return option !== minValue && option !== maxValue
  }

  return (
    <Autocomplete
      options={options}
      multiple
      disableCloseOnSelect
      renderTags={getRangeDisplayValue}
      size={size ?? 'medium'}
      renderInput={params => <TextField {...params} label={label} />}
      value={fieldValues}
      onChange={(e, value) => handleValueChange(value)}
      getOptionDisabled={getRangeDisabledOptions}
      fullWidth
      data-cy={dataCy}
    />
  )
}
